/**
 * Hook de tema (light/dark/system) con persistencia en localStorage.
 *
 * Default: respeta `prefers-color-scheme` del sistema operativo.
 * El usuario puede override con "light" o "dark" explícito.
 *
 * Aplica la clase `dark` al elemento <html> cuando el tema efectivo
 * es dark, para que Tailwind y Tremor apliquen sus estilos dark.
 */
import { useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "restaurant-dashboards:theme";

function getSystemTheme(): EffectiveTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

function effectiveThemeFor(mode: ThemeMode): EffectiveTheme {
  return mode === "system" ? getSystemTheme() : mode;
}

function applyThemeToDom(theme: EffectiveTheme) {
  if (typeof document === "undefined") return;
  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [systemTheme, setSystemTheme] = useState<EffectiveTheme>(() => getSystemTheme());

  // Aplica el tema al <html> cada vez que mode o systemTheme cambian
  useEffect(() => {
    const effective = effectiveThemeFor(mode);
    applyThemeToDom(effective);
  }, [mode, systemTheme]);

  // Si el modo es "system", escucha cambios del SO
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  function setMode(next: ThemeMode) {
    setModeState(next);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, next);
    }
  }

  function cycleMode() {
    // light → dark → system → light
    const order: ThemeMode[] = ["light", "dark", "system"];
    const idx = order.indexOf(mode);
    const next = order[(idx + 1) % order.length];
    setMode(next);
  }

  return {
    mode,
    setMode,
    cycleMode,
    effective: effectiveThemeFor(mode),
  };
}
